import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  runTransaction,
  increment,
  limit,
  deleteField
} from 'firebase/firestore'
import type { User } from 'firebase/auth'
import { db, auth, getActiveAuth, getActiveDb } from '../config/firebase'
import type { 
  AccountDocument, 
  AccountFormData, 
  CreateAccountData,
  ProfileDocument,
  UsageData 
} from '../types/account'
import { profileAccessService } from './profileAccessService'
import { initializeSessionData, getRolePermissions } from './sessionService'
import { transformKeysToSnake, transformKeysToCamel } from '../utils/namingConventions'
import { loggingService, LogEventType } from './loggingService'
import { API_BASE_URL } from '../config/api'

// Missing utility functions
const generateDefaultPhoto = (): string => {
  // Generate a default avatar URL or return a placeholder
  return `https://ui-avatars.com/api/?name=User&background=random&color=fff&size=128`
}

const tokenizeAutocomplete = (text: string): string[] => {
  if (!text) return []
  
  // Simple tokenization for autocomplete - split by spaces and create variations
  const tokens = text.toLowerCase().split(/\s+/)
  const variations: string[] = []
  
  // Add original tokens
  variations.push(...tokens)
  
  // Add combinations (e.g., "john doe" -> ["john", "doe", "john doe"])
  for (let i = 0; i < tokens.length; i++) {
    for (let j = i + 1; j <= tokens.length; j++) {
      const combination = tokens.slice(i, j).join(' ')
      if (combination.length > 0) {
        variations.push(combination)
      }
    }
  }
  
  // Remove duplicates and return
  return [...new Set(variations)]
}

// Utility functions
const generateUsernameFromName = (name: string): string => {
  if (!name) return ''
  return name.toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9.-]/g, '')
}

/**
 * Generate a unique username with collision detection
 * This is called outside of transactions to avoid blocking
 */
const generateUniqueUsernameForTransaction = async (baseName: string): Promise<string> => {
  const baseUsername = generateUsernameFromName(baseName)
  if (!baseUsername) return `user-${Date.now().toString(36)}`
  
  // First, try the base username without any suffix
  const baseAvailable = await isUsernameAvailable(baseUsername)
  if (baseAvailable) {
    return baseUsername
  }
  
  // If we can't check availability (auth issues), use timestamp-based fallback
  if (baseAvailable === null) {
    const timestamp = Date.now().toString(36)
    const randomSuffix = Math.random().toString(36).substring(2, 4)
    const fallbackUsername = `${baseUsername}_${timestamp}${randomSuffix}`
    console.log(`Using fallback username due to auth issues: ${fallbackUsername}`)
    return fallbackUsername
  }
  
  // If base username is taken, try with numeric suffixes
  for (let i = 1; i <= 999; i++) {
    const candidateUsername = `${baseUsername}${i}`
    const available = await isUsernameAvailable(candidateUsername)
    if (available) {
      return candidateUsername
    }
    // If we can't check availability, fall back to timestamp
    if (available === null) {
      const timestamp = Date.now().toString(36)
      const randomSuffix = Math.random().toString(36).substring(2, 4)
      const fallbackUsername = `${baseUsername}_${timestamp}${randomSuffix}`
      console.log(`Using fallback username due to auth issues: ${fallbackUsername}`)
      return fallbackUsername
    }
  }
  
  // Fallback to timestamp + random if all numeric suffixes are taken
  const timestamp = Date.now().toString(36)
  const randomSuffix = Math.random().toString(36).substring(2, 4)
  const fallbackUsername = `${baseUsername}_${timestamp}${randomSuffix}`
  return fallbackUsername
}

/**
 * Check if a username is available (not already taken)
 * Returns true if available, false if taken, null if unable to check (auth issues)
 */
const isUsernameAvailable = async (username: string): Promise<boolean | null> => {
  try {
    
    // Check the dedicated usernames collection instead of querying profiles
    // This is more efficient and secure
    const usernameRef = doc(getActiveDb(), 'usernames', username)
    const usernameSnap = await getDoc(usernameRef)
    
    const isAvailable = !usernameSnap.exists()
    
    return isAvailable
  } catch (error) {
    console.error('Error checking username availability:', error)
    
    // Log more details about the error for debugging
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        code: (error as any).code,
        name: error.name
      })
    }
    
    // Check if this is a permission/auth error
    const errorCode = (error as any)?.code
    if (errorCode === 'permission-denied' || errorCode === 'unauthenticated' || 
        (error as Error)?.message?.includes('Missing or insufficient permissions')) {
      console.log(`Cannot check username availability due to auth issues: ${username}`)
      return null // Signal that we can't check due to auth issues
    }
    
    // For other errors, assume it's taken to be safe
    return false
  }
}

const generateProfileId = (): string => {
  // Generate 8-character random ID (much shorter than UUID)
  // Format: "a7x9k2m1" - gives 2.8 trillion possible combinations
  return Math.random().toString(36).substring(2, 10)
}

/**
 * Create a username document to track username usage
 */
const createUsernameDocument = (batch: any, username: string, profileId: string, userId: string): void => {
  const usernameRef = doc(getActiveDb(), 'usernames', username)
  const camelCaseData = {
    ownerUid: userId,
    profileId: profileId,
    createdAt: serverTimestamp()
  }
  batch.set(usernameRef, transformKeysToSnake(camelCaseData))
}

const prepareUsageForAccountType = (plan: string = 'free'): UsageData => {
  // Updated limits to match new pricing structure (2025 update)
  const defaultLimits = {
    free: { storage: 200, uploads: 2, tokens: 150_000, wholeGenomeData: { canUpload: false } },
    basic: { storage: 5000, uploads: 10, tokens: 500_000, wholeGenomeData: { canUpload: true } },
    premium: { storage: 10000, uploads: 50, tokens: 1_000_000, wholeGenomeData: { canUpload: true } },
    pro: { storage: 50000, uploads: 200, tokens: 3_000_000, wholeGenomeData: { canUpload: true } },
    // Legacy plans for backward compatibility
    premium_plus: { storage: 10000, uploads: 200, tokens: 100000, wholeGenomeData: { canUpload: true } },
    legacy: { storage: 1000, uploads: 25, tokens: 10000, wholeGenomeData: { canUpload: true } }
  }

  const limits = defaultLimits[plan as keyof typeof defaultLimits] || defaultLimits.free

  return {
    usedStorage: 0,
    storageLimit: limits.storage,
    allowedTokens: limits.tokens,
    usedTokens: 0,
    additionalTokens: 0
  }
}

/**
 * Get account document from Firestore
 */
export const getAccountDocument = async (userId: string): Promise<AccountDocument | null> => {
  try {
    const accountRef = doc(getActiveDb(), 'accounts', userId)
    const accountSnap = await getDoc(accountRef)
    
    if (accountSnap.exists()) {
      // Convert snake_case Firestore data to camelCase for frontend
      const rawData = accountSnap.data()
      const data = transformKeysToCamel(rawData) as Record<string, any>
      
      // Helper function to safely convert Timestamp to Date
      const convertTimestamp = (timestamp: any): Date | undefined => {
        if (!timestamp) return undefined
        if (timestamp instanceof Timestamp) return timestamp.toDate()
        if (timestamp instanceof Date) return timestamp
        const date = new Date(timestamp)
        return isNaN(date.getTime()) ? undefined : date
      }

      // Ensure required nested objects exist (frontend expects these)
      const subscription = data.subscription || { active: false }
      const usage = data.usage || { usedStorage: 0, storageLimit: 0, allowedTokens: 0, usedTokens: 0, additionalTokens: 0 }
      const preferences = data.preferences || { theme: 'light', locale: 'en-US', notifications: { email: true, push: true, sms: false } }
      const features = data.features || { collaboration: true, advancedReports: false, geneticAnalysis: true, dataExport: false, apiAccess: false, betaFeatures: false }
      const roles = data.roles || { system: 'user', subscription: data.plan || 'free' }

      return {
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                   data.created instanceof Timestamp ? data.created.toDate() : new Date(data.createdAt || data.created),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : 
                   data.modified instanceof Timestamp ? data.modified.toDate() : new Date(data.updatedAt || data.modified),
        legacyCreatedAt: data.legacyCreatedAt instanceof Timestamp ? data.legacyCreatedAt.toDate() :
                         data.legacyCreatedAt ? new Date(data.legacyCreatedAt) : undefined,
        subscription: {
          ...subscription,
          expiresAt: subscription?.expiresAt instanceof Timestamp 
            ? subscription.expiresAt.toDate() 
            : subscription?.expiresAt ? new Date(subscription.expiresAt) : undefined
        },
        usage,
        preferences,
        features,
        roles,
        security: data.security ? {
          ...data.security,
          lastPasswordChange: convertTimestamp(data.security.lastPasswordChange)
        } : undefined,
        compliance: data.compliance ? {
          ...data.compliance,
          consentUpdatedAt: convertTimestamp(data.compliance.consentUpdatedAt || data.compliance.consent_updated_at),
          termsAcceptedAt: convertTimestamp(data.compliance.termsAcceptedAt || data.compliance.terms_accepted_at),
          termsVersion: data.compliance.termsVersion || data.compliance.terms_version,
          termsAcceptanceMethod: data.compliance.termsAcceptanceMethod || data.compliance.terms_acceptance_method,
          partnerTermsAcceptedAt: convertTimestamp(
            data.compliance.partnerTermsAcceptedAt || data.compliance.partner_terms_accepted_at
          ),
          partnerRoleAckAt: convertTimestamp(
            data.compliance.partnerRoleAckAt || data.compliance.partner_role_ack_at
          ),
          clientWaiverAcceptedAt: convertTimestamp(
            data.compliance.clientWaiverAcceptedAt || data.compliance.client_waiver_accepted_at
          ),
          geneticDataConsentAt: convertTimestamp(
            data.compliance.geneticDataConsentAt || data.compliance.genetic_data_consent_at
          ),
          geneticDataConsentVersion:
            data.compliance.geneticDataConsentVersion || data.compliance.genetic_data_consent_version,
          geneticDataConsentMethod:
            data.compliance.geneticDataConsentMethod || data.compliance.genetic_data_consent_method,
          needsGeneticConsentBackfill: Boolean(
            data.compliance.needsGeneticConsentBackfill ?? data.compliance.needs_genetic_consent_backfill
          ),
        } : undefined
      } as AccountDocument
    }
    
    return null
  } catch (error) {
    console.error('Error fetching account document:', error)
    throw new Error('Failed to fetch account information')
  }
}

/**
 * Create account document and default profile for new user (matching V3 behavior)
 * This is now used only for testing or special cases - use checkAndSetupNewUser for production
 */
export const createAccountAndProfile = async (
  user: User,
  options: CreateAccountData = {
    uid: user.uid,
    email: user.email || '',
    name: user.displayName || 'User'
  }
): Promise<{ account: AccountDocument; profile: ProfileDocument }> => {
  try {
    const batch = writeBatch(getActiveDb())
    
    // Create account document with camelCase fields
    const camelCaseAccountData: Omit<AccountDocument, 'createdAt' | 'updatedAt'> & {
      createdAt: any
      updatedAt: any
    } = {
      schemaVersion: 5,
      uid: user.uid,
      name: user.displayName || 'User',
      email: user.email || '',
      state: 'active',
      plan: 'free',
      roles: {
        system: 'user',
        subscription: 'free'
      },
      usage: prepareUsageForAccountType('free'),
      subscription: { active: false },
      sessionData: {
        lastLogin: new Date(),
        loginCount: 0,
        totalSessionsAllTime: 0,
        devices: [],
        activity: {
          lastActivityAt: new Date(),
          averageDailyUsage: 0,
          preferredFeatures: [],
          inactiveDays: 0
        }
      },
      preferences: {
        theme: 'light',
        locale: 'en-US',
        notifications: {
          email: true,
          push: true,
          sms: false
        }
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // V4 Collaboration features enabled by default
      features: {
        collaboration: true,
        advancedReports: false,
        geneticAnalysis: true,
        dataExport: false,
        apiAccess: false,
        betaFeatures: false
      }
    }
    
    // Create default profile (matching V3 Profile model)
    const profileId = generateProfileId()
    const username = await generateUniqueUsernameForTransaction(options.name)
    
    const camelCaseProfile: any = {
      name: options.name,
      ownerUid: user.uid,
      profileId: profileId,
      default: true,
      username: username,
      photo: '',
      ancestry: 'global',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    // Convert both account and profile data to snake_case for Firestore storage
    const accountData = transformKeysToSnake(camelCaseAccountData)
    const profileData = transformKeysToSnake(camelCaseProfile)
    
    // Add default_pid to account (already in snake_case)
    ;(accountData as any).default_pid = profileId
    
    // Batch write all documents
    const accountRef = doc(getActiveDb(), 'accounts', user.uid)
    const profileRef = doc(getActiveDb(), 'profiles', profileId)
    
    batch.set(accountRef, accountData)
    batch.set(profileRef, profileData)
    
    // Create username document
    createUsernameDocument(batch, username, profileId, user.uid)
    
    await batch.commit()
    
    // Return created documents with current timestamps (using camelCase data for frontend)
    const now = new Date()

    // Create owner access and index for the default profile so it appears in the profiles directory
    try {
      await profileAccessService.createOwnerAccess(profileId, user.uid, {
        name: options.name || 'User',
        username,
        photo: generateDefaultPhoto(),
        ownerName: options.name || 'User',
        ownerUid: user.uid
      })
    } catch (err) {
      console.error('Error creating owner access/index for default profile:', err)
      // Do not throw; account/profile already created. Caller can sync index later if needed.
    }
    return {
      account: {
        ...camelCaseAccountData,
        createdAt: now,
        updatedAt: now
      },
      profile: {
        ...camelCaseProfile,
        createdAt: now,
        updatedAt: now
      }
    }
  } catch (error) {
    console.error('Error creating account and profile:', error)
    throw new Error('Failed to create account and profile')
  }
}

/**
 * Get user's profiles
 */
export const getUserProfiles = async (userId: string): Promise<ProfileDocument[]> => {
  try {
    const profilesRef = collection(getActiveDb(), 'profiles')
    const q = query(profilesRef, where('owner_uid', '==', userId))
    const querySnapshot = await getDocs(q)
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      // Convert snake_case to camelCase for frontend compatibility
      const camelCaseData = transformKeysToCamel(data)
      
      return {
        ...camelCaseData,
        // Handle timestamp conversions using camelCase data
        createdAt: camelCaseData.createdAt instanceof Timestamp ? camelCaseData.createdAt.toDate() : 
                   camelCaseData.created instanceof Timestamp ? camelCaseData.created.toDate() : new Date(camelCaseData.createdAt || camelCaseData.created),
        updatedAt: camelCaseData.updatedAt instanceof Timestamp ? camelCaseData.updatedAt.toDate() : 
                   camelCaseData.modified instanceof Timestamp ? camelCaseData.modified.toDate() : new Date(camelCaseData.updatedAt || camelCaseData.modified),
        lastViewed: camelCaseData.lastViewed instanceof Timestamp ? camelCaseData.lastViewed.toDate() : (camelCaseData.lastViewed ? new Date(camelCaseData.lastViewed) : undefined)
      } as ProfileDocument
    })
  } catch (error) {
    console.error('Error fetching user profiles:', error)
    throw new Error('Failed to fetch user profiles')
  }
}

/**
 * Get a single profile by ID with proper access control (backend enforced)
 * This checks both ownership and shared access permissions
 */
export const getProfileById = async (profileId: string): Promise<ProfileDocument | null> => {
  try {
    const user = getActiveAuth().currentUser
    if (!user) {
      throw new Error('User not authenticated')
    }

    const token = await user.getIdToken()
    const response = await fetch(`${API_BASE_URL}/api/v1/profiles/${profileId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.status === 404) {
      return null // Profile not found
    }

    if (response.status === 403) {
      return null // Access denied
    }

    if (!response.ok) {
      // Handle 403/404 gracefully if user might have shared access
      if (response.status === 403 || response.status === 404) {
        console.log(`Backend returned ${response.status} for profile ${profileId}, checking shared access locally...`)
        try {
          // Check if we have access via profileAccessService (which checks shared access collections)
          const accessDetails = await profileAccessService.getUserAccessDetails(profileId, user.uid)
          
          // If we get here, user DOES have access (getUserAccessDetails throws if not)
          // We need to construct a partial ProfileDocument from what we can access
          // Note: We won't have all fields like raw profile data if backend blocked it,
          // but we should have enough for basic display.
          // Ideally, the backend 403 should return the partial profile data allowed.
          // If the backend returned 404, the profile might not exist at all, or is hidden.
          
          // For now, if backend says NO but we find access doc, we can try to return a minimal profile
          // constructed from the access metadata if available.
          
          // Try to get profile metadata from the user's access index
          const accessIndex = await profileAccessService.getUserAccessibleProfiles(user.uid)
          // NOTE: The accessIndex array contains profileIds in snake_case if raw, but our service transforms them
          // The service returns ProfileAccessIndex[] which has camelCase profileId
          const profileIndex = accessIndex.find(p => p.profileId === profileId)
          
          if (profileIndex) {
            // Construct a minimal profile document
            // We might not have all fields, but we have enough for display
            const profileDoc: ProfileDocument = {
              profileId: profileId,
              userId: user.uid, // Current user is viewing it
              name: profileIndex.profileName || 'Shared Profile',
              ownerUid: profileIndex.ownerUid || '',
              username: profileIndex.profileUsername || '',
              relationship: undefined,
              birthDate: undefined,
              gender: undefined,
              ancestry: 'global',
              notes: undefined,
              photo: profileIndex.profilePhoto,
              profilePic: profileIndex.profilePhoto,
              default: false,
              isActive: true,
              createdAt: profileIndex.accessGrantedAt instanceof Date ? profileIndex.accessGrantedAt : new Date(),
              updatedAt: (profileIndex.lastAccessed instanceof Date ? profileIndex.lastAccessed : new Date()),
            }
            return profileDoc
          } else {
             // If not found in index, try fetching the access doc directly to double check
             // This covers race conditions where index might be slightly behind
             // We already fetched accessDetails above successfully, so we know access exists.
             
             // If we are here, we have accessDetails but no index entry? That's rare but possible.
             // Let's construct from accessDetails if possible, but we miss profile name/photo 
             // unless they were denormalized onto the access doc (which they aren't usually).
             // However, profileAccessService.getUserAccessDetails returns permissions, not profile metadata.
             
             // Fallback: Try to fetch public profile info if available, or return a placeholder
             // Since backend 403'd, we can't get public info easily unless there's a separate public endpoint.
             
             // Worst case: return a placeholder so they can at least see "Shared Profile" and access tools
             return {
              profileId: profileId,
              userId: user.uid,
              name: 'Shared Profile', // We don't know the name if not in index
              ownerUid: '', // Unknown
              username: '',
              ancestry: 'global',
              photo: '',
              profilePic: '',
              default: false,
              isActive: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            } as ProfileDocument
          }
        } catch (accessError) {
          // Real access denied or profile truly missing
          console.warn('Local shared access check failed:', accessError)
        }
        return null // Valid 403/404
      }

      let errorMessage = 'Failed to fetch profile'
      try {
        const errorData = await response.json()
        errorMessage = errorData.detail || errorData.message || errorMessage
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text()
          errorMessage = errorText || errorMessage
        } catch (textError) {
          // If text parsing also fails, use default message
          errorMessage = `HTTP ${response.status}: ${response.statusText}`
        }
      }
      
      console.error('Error fetching profile by ID:', {
        status: response.status,
        statusText: response.statusText,
        message: errorMessage,
        profileId
      })
      
      // For 500 errors, throw the error so the caller can handle it
      if (response.status >= 500) {
        throw new Error(`Server error: ${errorMessage}`)
      }
      
      // For other errors, return null (treat as not found/access denied)
      return null
    }

    const data = await response.json()
    
    // Transform to ProfileDocument format
    const profileDoc: ProfileDocument = {
      profileId: data.profile_id || profileId,
      userId: user.uid,
      name: data.name || '',
      ownerUid: data.owner_uid || user.uid,
      username: data.username || '',
      relationship: data.relationship,
      birthDate: data.date_of_birth,
      gender: data.gender,
      genderSource: data.gender_source,
      ancestry: data.ancestry || 'global',
      ancestrySource: data.ancestry_source,
      ancestryConfidence: data.ancestry_confidence,
      ancestryProportions: data.ancestry_proportions ?? undefined,
      hasRawdata: data.has_rawdata || false,
      notes: data.notes,
      photo: data.photo,
      profilePic: data.profile_pic || data.photo,
      default: data.is_default || false,
      isActive: true,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
      updatedAt: data.modified_at ? new Date(data.modified_at) : new Date(),
    }

    return profileDoc
  } catch (error) {
    console.error('Error fetching profile by ID:', {
      error,
      profileId,
      message: error instanceof Error ? error.message : String(error)
    })
    // Re-throw server errors so the UI can display them
    if (error instanceof Error && error.message.includes('Server error')) {
      throw error
    }
    return null
  }
}

/**
 * Pagination metadata for profile lists
 */
export interface ProfilePaginationMetadata {
  hasMore: boolean
  nextCursor: string | null
  count: number
}

/**
 * Get all profiles visible to the user (owned + shared via access index)
 * Supports pagination for better performance with large profile lists
 */
export const getAllProfilesForUser = async (
  userId: string,
  options?: {
    limit?: number
    startAfter?: string
  }
): Promise<{
  profiles: ProfileDocument[]
  pagination?: ProfilePaginationMetadata
}> => {
  try {
    // If pagination is requested, use backend API for owned profiles
    // and combine with shared profiles from access index
    if (options?.limit !== undefined) {
      const user = getActiveAuth().currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const token = await user.getIdToken()
      const params = new URLSearchParams()
      if (options.limit) params.set('limit', options.limit.toString())
      if (options.startAfter) params.set('start_after', options.startAfter)

      // Get owned profiles from backend API
      const response = await fetch(`${API_BASE_URL}/api/v1/profiles?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        console.error('Failed to fetch profiles from API:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        })
        throw new Error(`Failed to fetch profiles: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      console.log('[getAllProfilesForUser] API response:', { 
        isArray: Array.isArray(data),
        hasProfiles: !!(data.profiles),
        profilesLength: Array.isArray(data) ? data.length : (data.profiles?.length || 0),
        dataKeys: Object.keys(data || {}),
        fullData: data
      })
      
      // Handle both old format (array) and new format (object with profiles array)
      const profilesArray = Array.isArray(data) ? data : (data.profiles || [])
      
      if (profilesArray.length === 0) {
        console.warn('[getAllProfilesForUser] No profiles in response', { 
          data,
          profilesArray,
          dataType: typeof data,
          isArray: Array.isArray(data),
          hasProfilesKey: 'profiles' in (data || {})
        })
      }
      
      const ownedProfiles: ProfileDocument[] = profilesArray.map((p: any) => ({
        profileId: p.profile_id,
        userId: userId,
        name: p.name || 'Profile',
        ownerUid: p.owner_uid,
        username: p.username || '',
        relationship: p.relationship,
        birthDate: p.date_of_birth,
        gender: p.gender,
        ancestry: p.ancestry || 'global',
        default: p.is_default || false,
        createdAt: p.created_at ? new Date(p.created_at) : new Date(),
        updatedAt: p.modified_at ? new Date(p.modified_at) : new Date(),
        photo: p.photo || p.profile_pic,
        profilePic: p.profile_pic || p.photo,
        notes: p.notes,
        isActive: true,
      } as ProfileDocument))

      // For pagination, we only return owned profiles from backend
      // Shared profiles would need separate pagination or be included in a different way
      // For now, return owned profiles with pagination metadata
      return {
        profiles: ownedProfiles,
        pagination: {
          hasMore: Array.isArray(data) ? false : (data.has_more || false),
          nextCursor: Array.isArray(data) ? null : (data.next_cursor || null),
          count: Array.isArray(data) ? ownedProfiles.length : (data.count || ownedProfiles.length)
        }
      }
    }

    // No pagination requested - use original logic for backward compatibility
    // Single directory source of truth: accounts/{uid}/profile_access
    const accessible = await profileAccessService.getUserAccessibleProfiles(userId)

    // Fetch actual profile documents to get correct default values
    const profilePromises = accessible.map(async (idx) => {
      try {
        const profileRef = doc(getActiveDb(), 'profiles', idx.profileId)
        const profileSnap = await getDoc(profileRef)
        
        if (!profileSnap.exists()) {
          // Profile doesn't exist, return basic info from index
          return {
            profileId: idx.profileId,
            userId: userId,
            name: idx.profileName || 'Profile',
            ownerUid: idx.ownerUid,
            username: idx.profileUsername || '',
            photo: idx.profilePhoto,
            profilePic: idx.profilePhoto,
            createdAt: idx.accessGrantedAt instanceof Date ? idx.accessGrantedAt : new Date(idx.accessGrantedAt as any),
            updatedAt: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : (idx.accessGrantedAt as any),
            lastViewed: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : undefined,
            isActive: true,
            default: false, // Default to false if profile doesn't exist
          } as ProfileDocument
        }
        
        const rawProfileData = profileSnap.data()
        const profileData = transformKeysToCamel(rawProfileData) as ProfileDocument
        
        // Merge with access index data, preserving the actual default value from the profile document
        return {
          ...profileData,
          // profileId must always come from the index (doc.id) — profile docs written
          // by older code paths may not have a profile_id field, causing profileId to
          // be undefined after transformKeysToCamel and breaking profile switching.
          profileId: idx.profileId,
          userId: userId,
          // Ensure we have the latest data from access index
          name: idx.profileName || profileData.name || 'Profile',
          username: idx.profileUsername || profileData.username || '',
          photo: idx.profilePhoto || profileData.photo,
          profilePic: idx.profilePhoto || profileData.profilePic,
          ownerUid: idx.ownerUid || profileData.ownerUid,
          // Keep the actual default value from the profile document
          default: profileData.default || false,
          // Add record restrictions info from access index
          hasRecordRestrictions: (idx as any).hasRecordRestrictions || false,
        } as ProfileDocument
      } catch (error) {
        console.error(`Error fetching profile ${idx.profileId}:`, error)
        // Return basic info from index if profile fetch fails
        return {
          profileId: idx.profileId,
          userId: userId,
          name: idx.profileName || 'Profile',
          ownerUid: idx.ownerUid,
          username: idx.profileUsername || '',
          photo: idx.profilePhoto,
          profilePic: idx.profilePhoto,
          createdAt: idx.accessGrantedAt instanceof Date ? idx.accessGrantedAt : new Date(idx.accessGrantedAt as any),
          updatedAt: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : (idx.accessGrantedAt as any),
          lastViewed: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : undefined,
          isActive: true,
          default: false, // Default to false if profile fetch fails
          hasRecordRestrictions: (idx as any).hasRecordRestrictions || false,
        } as ProfileDocument
      }
    })

    const profiles = await Promise.all(profilePromises)
    return { profiles }
  } catch (error) {
    console.error('Error getting all profiles for user:', error)
    // If pagination was requested and it failed, still return empty array
    // Otherwise, the error is already logged
    if (options?.limit !== undefined) {
      console.error('Pagination request failed, returning empty profiles array')
    }
    return { profiles: [] }
  }
}

/**
 * Get recent profiles for navigation (up to 5, sorted by last_viewed descending)
 * This matches the V3 behavior from get_profiles_for_nav
 */
export const getRecentProfilesForNav = async (userId: string): Promise<ProfileDocument[]> => {
  try {
    if (!userId) return []

    const accessibleIdx = await profileAccessService.getUserAccessibleProfiles(userId)
    const mapped: ProfileDocument[] = accessibleIdx.map((idx) => ({
      profileId: idx.profileId,
      userId: userId,
      name: idx.profileName || 'Profile',
      ownerUid: idx.ownerUid,
      username: idx.profileUsername || '',
      photo: idx.profilePhoto,
      profilePic: idx.profilePhoto,
      createdAt: idx.accessGrantedAt instanceof Date ? idx.accessGrantedAt : new Date(idx.accessGrantedAt as any),
      updatedAt: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : (idx.accessGrantedAt as any),
      lastViewed: (idx.lastAccessed as any) ? ((idx.lastAccessed as any) instanceof Date ? (idx.lastAccessed as any) : new Date(idx.lastAccessed as any)) : undefined,
      isActive: true,
    }))

    // Normalize potentially mixed date-like values (Date, Firestore Timestamp, string) to milliseconds
    const toMs = (v: any): number => {
      if (!v) return 0
      if (v instanceof Date) return v.getTime()
      if (typeof (v as any).toDate === 'function') {
        const d = (v as any).toDate()
        return d instanceof Date ? d.getTime() : 0
      }
      const d = new Date(v as any)
      return isNaN(d.getTime()) ? 0 : d.getTime()
    }

    mapped.sort((a, b) => {
      const aMs = toMs(a.lastViewed || a.updatedAt)
      const bMs = toMs(b.lastViewed || b.updatedAt)
      return bMs - aMs
    })

    return mapped.slice(0, 5)
    
  } catch (error) {
    console.error('Error fetching recent profiles for navigation:', error)
    
    // More detailed error information
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      })
    }
    
    // Return empty array instead of throwing to prevent breaking the UI
    return []
  }
}

/**
 * Update profile's last_viewed timestamp
 */
export const updateProfileLastViewed = async (profileId: string): Promise<void> => {
  try {
    // Update the user's access index rather than the profile document to respect permissions
    const userId = getActiveAuth().currentUser?.uid
    if (!userId) {
      throw new Error('User not authenticated')
    }
    await profileAccessService.updateLastAccessed(profileId, userId)
  } catch (error) {
    console.error('Error updating profile last viewed:', error)
    throw new Error('Failed to update profile last viewed')
  }
}

/**
 * Check if user needs first-time setup and perform it atomically using transactions
 * This prevents race conditions and ensures consistency
 */
export const checkAndSetupNewUser = async (user: User): Promise<{
  isNewUser: boolean
  account?: AccountDocument
  profile?: ProfileDocument
}> => {
  // Preflight gate: if we already have an account doc and at least one accessible profile,
  // skip calling the backend setup endpoint. This avoids unnecessary setup-user calls on
  // each auth state change while keeping idempotency.

  // Track onboarding status to preserve it after backend calls
  let preservedOnboardingStatus: { onboardingCompleted?: boolean; onboardingSkipped?: boolean } | null = null

  try {
    // Use a single canonical API base URL. Must be set in env.
    const API_BASE_URL_LOCAL = API_BASE_URL

    // 1) Quick Firestore checks to see if user is already provisioned
    try {
      const existingAccount = await getAccountDocument(user.uid)

      if (existingAccount) {
        // Check onboarding status from account document
        const onboardingCompleted = existingAccount.onboardingCompleted || false
        const onboardingSkipped = existingAccount.onboardingSkipped || false
        const onboardingDone = onboardingCompleted || onboardingSkipped
        
        if (onboardingDone) {
          // User has completed or skipped onboarding
          const result = await getAllProfilesForUser(user.uid)
          const accessible = result.profiles
          
          // Sort by last accessed (most recent first), fallback to accessGrantedAt
          const sortedByLastAccessed = [...accessible].sort((a, b) => {
            const aLastAccessed = a.lastViewed || a.updatedAt || a.createdAt
            const bLastAccessed = b.lastViewed || b.updatedAt || b.createdAt
            const aTime = aLastAccessed instanceof Date ? aLastAccessed.getTime() : new Date(aLastAccessed).getTime()
            const bTime = bLastAccessed instanceof Date ? bLastAccessed.getTime() : new Date(bLastAccessed).getTime()
            return bTime - aTime
          })
          const defaultProfile = sortedByLastAccessed[0]
          
          return {
            isNewUser: false,
            account: existingAccount,
            profile: defaultProfile || undefined,
          }
        } else {
          // User has account but hasn't completed or skipped onboarding
          // Preserve onboarding status to apply after backend call
          preservedOnboardingStatus = {
            onboardingCompleted: existingAccount.onboardingCompleted || false,
            onboardingSkipped: existingAccount.onboardingSkipped || false
          }
          // Fall through to backend call
        }
      }
    } catch (preflightErr) {
      // Non-fatal: fall through to backend setup
      console.warn('Preflight account/profile check failed; proceeding to setup-user:', preflightErr)
    }
    
    // Check if backend is ready before attempting setup
    // Optional health check; non-blocking
    try {
      await fetch(`${API_BASE_URL_LOCAL}/health`, { method: 'GET', signal: AbortSignal.timeout(1500) })
    } catch (_) {}
    // Retrieve a fresh ID token; occasionally the emulator returns an empty
    // string for a few ms right after page reload. Retry up to 3× before bailing
    let token: string | undefined = undefined
    let attempts = 0
    while (!token && attempts < 3) {
      token = await user.getIdToken()
      if (token) break
      await new Promise((r) => setTimeout(r, 300))
      attempts++
    }

    if (!token) {
      console.warn('🚫 No Firebase ID-token available – skipping setup-user call')
      return { isNewUser: false }
    }

    // Check for newsletter signup preference in localStorage
    const newsletterSubscribed = localStorage.getItem('signup_newsletter_pref') === 'true'
    const referrer = localStorage.getItem('lw_referrer') || undefined
    const pendingLegalMethod = sessionStorage.getItem('legal_acceptance_method')
    const isGoogleProvider = user.providerData?.some((p) => p.providerId === 'google.com')
    let legalAcceptanceMethod: 'signup' | 'google_signin_wrap' | undefined
    if (pendingLegalMethod === 'signup') {
      legalAcceptanceMethod = 'signup'
      sessionStorage.removeItem('legal_acceptance_method')
    } else if (isGoogleProvider) {
      legalAcceptanceMethod = 'google_signin_wrap'
    }

    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/auth/setup-user`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: user.displayName || user.email?.split('@')[0] || 'User',
        newsletter_subscribed: newsletterSubscribed,
        ...(referrer ? { referrer } : {}),
        ...(legalAcceptanceMethod ? { legal_acceptance_method: legalAcceptanceMethod } : {}),
      }),
      signal: AbortSignal.timeout(35000) // 35 second timeout (V2 legacy migration needs up to 30s for cross-project Datastore queries)
    })

    // Clear the preference after use
    if (newsletterSubscribed) {
      localStorage.removeItem('signup_newsletter_pref')
    }
    // Clear referrer after it has been captured on account creation
    if (referrer) {
      localStorage.removeItem('lw_referrer')
    }
    
    if (response.ok) {
      const result = await response.json()
      
      // Add small delay to allow Firestore writes to propagate
      if (result.is_new_user) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      // Merge preserved onboarding status if we have it
      let finalAccount = result.account
      if (preservedOnboardingStatus && finalAccount) {
        finalAccount = {
          ...finalAccount,
          onboardingCompleted: preservedOnboardingStatus.onboardingCompleted,
          onboardingSkipped: preservedOnboardingStatus.onboardingSkipped
        }
      }

      // Return the account and profile from backend response
      return {
        isNewUser: result.is_new_user,
        account: finalAccount,
        profile: result.profile
      }
    } else {
      const errorText = await response.text()
      throw new Error(`Backend setup failed: ${response.status} ${errorText}`)
    }
  } catch (error) {
    // Auto-fix: If setup-user times out/fails but account exists with profile,
    // mark onboarding as skipped to avoid future setup-user calls
    const isTimeoutError = error instanceof Error && (
      error.name === 'TimeoutError' || 
      error.message.includes('timed out') ||
      error.message.includes('signal timed out')
    )
    
    if (isTimeoutError) {
      try {
        // Check if account exists with profile but onboarding not marked
        const existingAccount = await getAccountDocument(user.uid)
        if (existingAccount) {
          const hasProfile = !!existingAccount.defaultProfileId
          const onboardingDone = existingAccount.onboardingCompleted || existingAccount.onboardingSkipped
          
          if (hasProfile && !onboardingDone) {
            // Account has profile but onboarding not marked - auto-fix it
            console.warn('[checkAndSetupNewUser] Auto-fixing onboarding status for account with profile', {
              uid: user.uid,
              hasProfile,
              onboardingDone
            })
            
            // Update Firestore to mark onboarding as skipped
            const accountRef = doc(getActiveDb(), 'accounts', user.uid)
            await updateDoc(accountRef, {
              onboardingSkipped: true
            })
            
            // Now retry the fast path - get account and profiles directly
            const result = await getAllProfilesForUser(user.uid)
            const accessible = result.profiles
            
            if (accessible.length > 0) {
              // Sort by last accessed
              const sortedByLastAccessed = [...accessible].sort((a, b) => {
                const aLastAccessed = a.lastViewed || a.updatedAt || a.createdAt
                const bLastAccessed = b.lastViewed || b.updatedAt || b.createdAt
                const aTime = aLastAccessed instanceof Date ? aLastAccessed.getTime() : new Date(aLastAccessed).getTime()
                const bTime = bLastAccessed instanceof Date ? bLastAccessed.getTime() : new Date(bLastAccessed).getTime()
                return bTime - aTime
              })
              
              // Return with updated account (onboardingSkipped now true)
              return {
                isNewUser: false,
                account: {
                  ...existingAccount,
                  onboardingSkipped: true
                },
                profile: sortedByLastAccessed[0] || undefined,
              }
            }
          }
        }
      } catch (autoFixError) {
        // If auto-fix fails, log but don't block - throw original error
        console.error('[checkAndSetupNewUser] Auto-fix failed:', autoFixError)
      }
    }
    
    // Re-throw original error if we couldn't auto-fix
    throw error
  }
}

/**
 * Mark onboarding as completed or skipped
 */
export const completeOnboarding = async (status: 'completed' | 'skipped'): Promise<void> => {
  try {
    const API_BASE_URL_LOCAL = API_BASE_URL
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }
    
    const token = await currentUser.getIdToken()
    
    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/auth/complete-onboarding`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to complete onboarding: ${response.status} ${errorText}`)
    }
    
    console.log(`Onboarding ${status} successfully`)
  } catch (error) {
    console.error('Error completing onboarding:', error)
    throw error
  }
}

/**
 * Update onboarding profile tour completion status
 */
export const updateOnboardingTourStatus = async (completed: boolean): Promise<void> => {
  try {
    const API_BASE_URL_LOCAL = API_BASE_URL
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }
    
    const token = await currentUser.getIdToken()
    
    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/users/onboarding-tour`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ completed })
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update onboarding tour status: ${response.status} ${errorText}`)
    }
    
  } catch (error) {
    console.error('Error updating onboarding tour status:', error)
    throw error
  }
}

/**
 * Update wellness goals page tour completion status
 */
export const updateWellnessGoalsTourStatus = async (completed: boolean): Promise<void> => {
  try {
    const API_BASE_URL_LOCAL = API_BASE_URL
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }
    
    const token = await currentUser.getIdToken()
    
    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/users/onboarding-tour/wellness-goals`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ completed })
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update wellness goals tour status: ${response.status} ${errorText}`)
    }
    
  } catch (error) {
    console.error('Error updating wellness goals tour status:', error)
    throw error
  }
}

/**
 * Update health records page tour completion status
 */
export const updateHealthRecordsTourStatus = async (completed: boolean): Promise<void> => {
  try {
    const API_BASE_URL_LOCAL = API_BASE_URL
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }
    
    const token = await currentUser.getIdToken()
    
    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/users/onboarding-tour/health-records`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ completed })
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update health records tour status: ${response.status} ${errorText}`)
    }
    
  } catch (error) {
    console.error('Error updating health records tour status:', error)
    throw error
  }
}

/**
 * Update custom reports tour completion status
 */
export const updateCustomReportsTourStatus = async (completed: boolean): Promise<void> => {
  try {
    const API_BASE_URL_LOCAL = API_BASE_URL
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }
    
    const token = await currentUser.getIdToken()
    
    const response = await fetch(`${API_BASE_URL_LOCAL}/api/v1/users/onboarding-tour/custom-reports`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ completed })
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update custom reports tour status: ${response.status} ${errorText}`)
    }
    
  } catch (error) {
    console.error('Error updating custom reports tour status:', error)
    throw error
  }
}

/**
 * Create a new account document for a newly registered user (legacy method)
 */
export const createAccountDocument = async (user: User): Promise<AccountDocument> => {
  const { account } = await createAccountAndProfile(user)
  return account
}

/**
 * Create a new profile for a user with V4 fields (V3 compatible)
 */
export const createProfileDocument = async (
  userId: string,
  profileData: {
    name: string
    relationship?: string
    birthDate?: string
    gender?: string
    ancestry?: string
    notes?: string
    defaultProfile?: boolean
    /** Set when the profile's date of birth places them under 18 at creation. */
    isMinor?: boolean
    /** ISO timestamp of the account holder's guardian-authority attestation. */
    guardianAttestedAt?: string
  }
): Promise<ProfileDocument> => {
  try {
    // Generate unique username and profile ID
    const uniqueUsername = await generateUniqueUsernameForTransaction(profileData.name)
    const profileId = generateProfileId()

    // Create profile data with camelCase field names, then convert to snake_case for Firestore
    const camelCaseProfileData: any = {
      name: profileData.name.trim(),
      ownerUid: userId,
      profileId: profileId,
      default: profileData.defaultProfile || false,
      isDefault: profileData.defaultProfile || false, // Add isDefault for backend compatibility
      username: uniqueUsername,
      photo: '',
      ancestry: (profileData.ancestry || 'global') as any,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    // Add V4 optional fields only if they have values (Firestore doesn't allow undefined)
    if (profileData.relationship && profileData.relationship.trim()) {
      camelCaseProfileData.relationship = profileData.relationship.trim()
    }
    if (profileData.birthDate && profileData.birthDate.trim()) {
      camelCaseProfileData.birthDate = profileData.birthDate.trim()
    }
    if (profileData.isMinor) {
      camelCaseProfileData.isMinor = true
    }
    if (profileData.guardianAttestedAt) {
      camelCaseProfileData.guardianAttestedAt = profileData.guardianAttestedAt
    }
    if (profileData.gender && profileData.gender.trim()) {
      camelCaseProfileData.gender = profileData.gender.trim()
    }
    if (profileData.notes && profileData.notes.trim()) {
      camelCaseProfileData.notes = profileData.notes.trim()
    }

    // Convert to snake_case for Firestore storage
    const firestoreProfileData = transformKeysToSnake(camelCaseProfileData)

    // Create profile using transaction to ensure consistency
    await runTransaction(getActiveDb(), async (transaction) => {
      const profileRef = doc(getActiveDb(), 'profiles', profileId)
      
      // Set the profile document
      transaction.set(profileRef, firestoreProfileData)

      // Create username document
      const usernameRef = doc(getActiveDb(), 'usernames', uniqueUsername)
      const camelCaseUsernameData = {
        ownerUid: userId,
        profileId: profileId,
        createdAt: serverTimestamp()
      }
      transaction.set(usernameRef, transformKeysToSnake(camelCaseUsernameData))

      // If this is set as default profile, update the user's account
      if (profileData.defaultProfile) {
        const accountRef = doc(getActiveDb(), 'accounts', userId)
        transaction.update(accountRef, {
          defaultPid: profileId,
          updatedAt: serverTimestamp()
        })
      }
    })

    // Create owner access for the profile (V4 collaboration system)
    try {
      // Get user info for the owner access
      const userDoc = await getDoc(doc(getActiveDb(), 'accounts', userId))
      const ownerName = userDoc.exists() ? userDoc.data().name : 'Unknown'
      
      await profileAccessService.createOwnerAccess(profileId, userId, {
        name: profileData.name,
        username: uniqueUsername,
        photo: generateDefaultPhoto(),
        ownerName,
        ownerUid: userId
      })
    } catch (error) {
      console.error('Error creating owner access:', error)
      // Don't fail the entire operation if access creation fails
    }

    // Return created profile with current timestamps (using camelCase data)
    const now = new Date()
    const createdProfile = {
      ...camelCaseProfileData,
      createdAt: now,
      updatedAt: now
    }

    // Log profile creation for audit trail
    try {
      await loggingService.logProfileCreated(profileId, profileData.name)
    } catch (logError) {
      console.warn('Failed to log profile creation:', logError)
      // Don't fail the main operation if logging fails
    }

    // Client-side create does not go through POST /profiles/, so drop the
    // 5-minute All Profiles cache or search will miss this profile.
    try {
      const user = getActiveAuth().currentUser
      if (user) {
        const token = await user.getIdToken()
        await fetch(`${API_BASE_URL}/api/v1/profiles/cache/invalidate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
      }
    } catch (cacheError) {
      console.warn('Failed to invalidate profiles cache after create:', cacheError)
    }

    return createdProfile
  } catch (error) {
    console.error('Error creating profile document:', error)
    throw new Error('Failed to create profile')
  }
}

/**
 * Read the coaching_mode preference for the current user.
 * Returns true (coaching on) if the field is absent — matches the backend default.
 */
export const getCoachingMode = async (userId: string): Promise<boolean> => {
  const account = await getAccountDocument(userId)
  if (!account) return true
  const prefs = account.preferences as any
  if (!prefs || prefs.coaching_mode === undefined) return true
  return Boolean(prefs.coaching_mode)
}

/**
 * Persist the coaching_mode preference for the current user.
 */
export const updateCoachingMode = async (userId: string, enabled: boolean): Promise<void> => {
  try {
    const accountRef = doc(getActiveDb(), 'accounts', userId)
    await updateDoc(accountRef, { 'preferences.coaching_mode': enabled })
  } catch (error) {
    console.error('Error updating coaching_mode:', error)
    throw new Error('Failed to update coaching mode')
  }
}

/**
 * Persist which genetic report view (Insights / Section Summary / SNP Table)
 * a member wants to land on. Set once from the report-view chooser page, and
 * changeable later from the "make this my default" action on the switcher.
 */
export const updatePreferredReportView = async (
  userId: string,
  view: 'insights' | 'summary' | 'snp_table',
): Promise<void> => {
  try {
    const accountRef = doc(getActiveDb(), 'accounts', userId)
    await updateDoc(accountRef, { 'preferences.preferred_report_view': view })
  } catch (error) {
    console.error('Error updating preferred_report_view:', error)
    throw new Error('Failed to update preferred report view')
  }
}

/**
 * Update account document with form data
 */
export const updateAccountDocument = async (
  userId: string, 
  formData: AccountFormData,
  currentVersion?: number
): Promise<void> => {
  try {
    const accountRef = doc(getActiveDb(), 'accounts', userId)
    
    const updateData: any = {
      name: formData.name,
      updatedAt: serverTimestamp()
    }
    
    // Only update version if this is a legacy account (version 3 or missing)
    if (!currentVersion || currentVersion < 4) {
      updateData.version = 4
    }
    
    await updateDoc(accountRef, transformKeysToSnake(updateData))
    
    // Log account update
    await loggingService.logEvent({
      eventType: LogEventType.ACCOUNT_UPDATED,
      userId: userId,
      details: {
        updatedFields: Object.keys(updateData).filter(key => key !== 'updated_at'),
        accountName: formData.name
      }
    })
  } catch (error) {
    console.error('Error updating account document:', error)
    throw new Error('Failed to update account information')
  }
}

/**
 * Update a profile document with new data
 */
export const updateProfileDocument = async (
  profileId: string,
  profileData: {
    name?: string
    username?: string
    relationship?: string
    birthDate?: string
    gender?: string
    ancestry?: string
    notes?: string
    defaultProfile?: boolean
    photo?: string
  }
): Promise<void> => {
  try {
    const user = getActiveAuth().currentUser
    if (!user) {
      throw new Error('User is not authenticated')
    }

    // Force token refresh to ensure we have a valid token
    const token = await user.getIdToken(true)
    const payload: Record<string, any> = {}

    if (profileData.name !== undefined) {
      const trimmed = profileData.name.trim()
      if (!trimmed) {
        throw new Error('Name cannot be empty')
      }
      payload.name = trimmed
    }

    if (profileData.username !== undefined) {
      const trimmed = profileData.username.trim()
      if (!trimmed) {
        throw new Error('Username cannot be empty')
      }
      if (!/^[a-z0-9._-]{3,30}$/i.test(trimmed)) {
        throw new Error('Username must be 3-30 characters and contain only letters, numbers, dots, underscores, or hyphens')
      }
      payload.username = trimmed
    }

    if (profileData.relationship !== undefined) {
      payload.relationship = profileData.relationship?.trim() || null
    }

    if (profileData.birthDate !== undefined) {
      payload.date_of_birth = profileData.birthDate?.trim() || null
    }

    if (profileData.gender !== undefined) {
      payload.gender = profileData.gender?.trim() || null
    }

    if (profileData.ancestry !== undefined) {
      payload.ancestry = profileData.ancestry?.trim() || 'global'
    }

    if (profileData.notes !== undefined) {
      payload.notes = profileData.notes?.trim() || null
    }

    if (profileData.defaultProfile !== undefined) {
      payload.is_default = profileData.defaultProfile
    }

    if (profileData.photo !== undefined) {
      payload.photo = profileData.photo || null
    }

    if (Object.keys(payload).length === 0) {
      throw new Error('No profile fields provided for update')
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/profiles/${profileId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      let errorMessage = 'Failed to update profile'
      try {
        const errorData = await response.json()
        errorMessage = errorData.detail || errorData.message || errorMessage
        console.error('Profile update API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          profileId,
          payloadKeys: Object.keys(payload)
        })
      } catch {
        try {
          const errorText = await response.text()
          errorMessage = errorText || errorMessage
          console.error('Profile update API error (text):', {
            status: response.status,
            statusText: response.statusText,
            errorText,
            profileId
          })
        } catch {
          // Ignore secondary parsing errors
        }
      }
      throw new Error(errorMessage)
    }
  } catch (error) {
    console.error('Error updating profile document via API:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Failed to update profile')
  }
}

/**
 * Re-run sex inference and ancestry estimation from existing genetic data.
 * Returns the updated gender and ancestry values so the UI can refresh.
 */
export interface GeneticReanalysisResult {
  gender?: string
  gender_source?: string
  ancestry?: string
  ancestry_source?: string
  ancestry_confidence?: number
  ancestry_proportions?: Record<string, number>
}

export const reanalyzeGenetics = async (profileId: string): Promise<GeneticReanalysisResult> => {
  const user = getActiveAuth().currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  const token = await user.getIdToken()
  const response = await fetch(`${API_BASE_URL}/api/v1/profiles/${profileId}/reanalyze-genetics`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Reanalysis failed (${response.status})`)
  }

  return await response.json()
}

/**
 * Delete a profile and all associated data atomically.
 * 
 * This function calls the backend API which ensures all critical artifacts
 * (profile document, username document, and profile_access index entries)
 * are deleted together in a single transaction. This prevents orphaned entries.
 * 
 * The backend handles:
 * - Atomic deletion of profile, username, and profile_access entries
 * - Deletion of all profile subcollections
 * - Deletion of external data (GCS, BigQuery, Pinecone)
 */
export const deleteProfile = async (profileId: string): Promise<void> => {
  try {
    const user = getActiveAuth().currentUser
    if (!user) {
      throw new Error('User not authenticated')
    }

    // First verify the profile exists and get its data for validation
    const profileRef = doc(getActiveDb(), 'profiles', profileId)
    const profileSnap = await getDoc(profileRef)
    
    if (!profileSnap.exists()) {
      throw new Error('Profile not found')
    }
    
    const rawProfileData = profileSnap.data()
    const profileData = transformKeysToCamel(rawProfileData) as ProfileDocument
    
    // Check if user owns this profile
    if (profileData.ownerUid !== user.uid) {
      throw new Error('You do not have permission to delete this profile')
    }
    
    // Check if this is the default profile
    if (profileData.default) {
      throw new Error('Cannot delete the default profile')
    }
    
    // Get all user's profiles to check if this is the last one
    const userProfiles = await getUserProfiles(user.uid)
    if (userProfiles.length <= 1) {
      throw new Error('Cannot delete the last remaining profile')
    }

    // Call backend API for atomic deletion
    const token = await user.getIdToken()
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.livewello.com'
    
    const response = await fetch(`${API_BASE_URL}/api/v1/profiles/${profileId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.detail || 'Failed to delete profile'
      throw new Error(errorMessage)
    }

    const result = await response.json()
    
    // Log profile deletion for audit trail
    try {
      await loggingService.logProfileDeleted(profileId, profileData.name)
    } catch (logError) {
      console.warn('Failed to log profile deletion:', logError)
      // Don't fail the deletion if logging fails
    }

    console.log('Profile deleted successfully via atomic deletion:', result)
    
  } catch (error) {
    console.error('Error deleting profile:', error)
    throw error
  }
}

/**
 * Delete all subcollections for a profile
 * This is a helper function that deletes all the subcollections under a profile
 */
const deleteProfileSubcollections = async (profileId: string): Promise<void> => {
  const subcollections = [
    'access',
    'activity_log', 
    'reports',
    'rawdata',
    'wellness_goals',
    'health_records',
    'documents',
    'messages', // Direct profile messages collection
    'uploads',
    'scheduled_reminders', // Add scheduled reminders cleanup
    'genetic_cache', // Add genetic cache cleanup
    'wellness_coach_sessions', // Wellness coach conversation sessions
    'goal_creation_sessions', // Goal creation conversation sessions
    'goal_checkin_sessions' // Goal check-in conversation sessions
  ]
  
  // Delete each subcollection
  for (const subcollectionName of subcollections) {
    await deleteSubcollection(profileId, subcollectionName)
  }
}

/**
 * Delete a specific subcollection for a profile
 */
const deleteSubcollection = async (profileId: string, subcollectionName: string): Promise<void> => {
  const subcollectionRef = collection(getActiveDb(), 'profiles', profileId, subcollectionName)
  const querySnapshot = await getDocs(subcollectionRef)
  
  // Process in batches to avoid hitting Firestore limits
  const batchSize = 500
  const batches = []
  
  for (let i = 0; i < querySnapshot.docs.length; i += batchSize) {
    const batch = writeBatch(getActiveDb())
    const batchDocs = querySnapshot.docs.slice(i, i + batchSize)
    
    for (const doc of batchDocs) {
      batch.delete(doc.ref)
      
      if (subcollectionName === 'messages') {
        // Handle feedback subcollection for each message in direct profile messages
        const feedbackRef = collection(doc.ref, 'feedback')
        const feedbackSnapshot = await getDocs(feedbackRef)
        feedbackSnapshot.docs.forEach(feedbackDoc => batch.delete(feedbackDoc.ref))
      }
      if (subcollectionName === 'health_records') {
        // delete nested notes subcollection
        const notesRef = collection(doc.ref, 'notes')
        const notesSnap = await getDocs(notesRef)
        notesSnap.docs.forEach(n => batch.delete(n.ref))
      }
      if (subcollectionName === 'wellness_goals') {
        // delete nested notes subcollection for wellness goals
        const notesRef = collection(doc.ref, 'notes')
        const notesSnap = await getDocs(notesRef)
        notesSnap.docs.forEach(n => batch.delete(n.ref))
      }
      if (subcollectionName === 'documents') {
        // delete nested notes and history subcollections for documents
        const notesRef = collection(doc.ref, 'notes')
        const notesSnap = await getDocs(notesRef)
        notesSnap.docs.forEach(n => batch.delete(n.ref))
        
        const historyRef = collection(doc.ref, 'history')
        const historySnap = await getDocs(historyRef)
        historySnap.docs.forEach(h => batch.delete(h.ref))
      }
      if (subcollectionName === 'genetic_cache') {
        // delete nested cache subcollections for genetic cache
        const cacheRef = collection(doc.ref, 'cache')
        const cacheSnap = await getDocs(cacheRef)
        cacheSnap.docs.forEach(c => batch.delete(c.ref))
      }
    }
    
    batches.push(batch.commit())
  }
  
  // Wait for all batches to complete
  await Promise.all(batches)
}

/**
 * Remove profile access entries from other users' accounts
 */
const removeProfileAccessEntries = async (profileId: string, userIds?: string[]): Promise<void> => {
  const batch = writeBatch(getActiveDb())

  if (userIds && userIds.length > 0) {
    for (const uid of userIds) {
      const profileAccessRef = doc(getActiveDb(), 'accounts', uid, 'profile_access', profileId)
      batch.delete(profileAccessRef)
    }
  } else {
    // Fallback: scan all accounts (slower, used only when we can't read access list)
    const accountsRef = collection(getActiveDb(), 'accounts')
    const accountsSnapshot = await getDocs(accountsRef)
    for (const accountDoc of accountsSnapshot.docs) {
      const profileAccessRef = doc(getActiveDb(), 'accounts', accountDoc.id, 'profile_access', profileId)
      batch.delete(profileAccessRef)
    }
  }

  await batch.commit()
}

/**
 * Check if a profile can be deleted
 * Returns validation info about whether the profile can be deleted
 */
export const canDeleteProfile = async (profileId: string): Promise<{
  canDelete: boolean
  reason?: string
}> => {
  try {
    if (!getActiveAuth().currentUser) {
      return { canDelete: false, reason: 'User not authenticated' }
    }
    
    // Get profile data
    const profileRef = doc(getActiveDb(), 'profiles', profileId)
    const profileSnap = await getDoc(profileRef)
    
    if (!profileSnap.exists()) {
      return { canDelete: false, reason: 'Profile not found' }
    }
    
    const rawProfileData = profileSnap.data()
    const profileData = transformKeysToCamel(rawProfileData) as ProfileDocument
    
    // Check if user owns this profile
    if (profileData.ownerUid !== getActiveAuth().currentUser.uid) {
      return { canDelete: false, reason: 'You do not have permission to delete this profile' }
    }
    
    // Check if this is the default profile
    if (profileData.default) {
      return { canDelete: false, reason: 'Cannot delete the default profile' }
    }
    
    // Get all user's profiles to check if this is the last one
    const userProfiles = await getUserProfiles(getActiveAuth().currentUser.uid)
    if (userProfiles.length <= 1) {
      return { canDelete: false, reason: 'Cannot delete the last remaining profile' }
    }
    
    return { canDelete: true }
  } catch (error) {
    console.error('Error checking if profile can be deleted:', error)
    return { canDelete: false, reason: 'Error checking profile deletion permissions' }
  }
}

/**
 * Get the default profile for a user
 * Used for navigation after profile deletion
 */
export const getDefaultProfile = async (userId: string): Promise<ProfileDocument | null> => {
  try {
    const userProfiles = await getUserProfiles(userId)
    return userProfiles.find(profile => profile.default) || null
  } catch (error) {
    console.error('Error getting default profile:', error)
    return null
  }
}

/**
 * Set a profile as the default profile for a user
 * This updates both the profile's default property and the account's default_pid field
 */
export const setProfileAsDefault = async (userId: string, profileId: string): Promise<void> => {
  try {
    // Verify the profile exists and belongs to the user
    const profileRef = doc(getActiveDb(), 'profiles', profileId)
    const profileSnap = await getDoc(profileRef)
    
    if (!profileSnap.exists()) {
      throw new Error('Profile not found')
    }
    
    const rawProfileData = profileSnap.data()
    const profileData = transformKeysToCamel(rawProfileData) as ProfileDocument
    
    if (profileData.ownerUid !== userId) {
      throw new Error('You do not have permission to modify this profile')
    }
    
    // Use a transaction to ensure atomicity
    await runTransaction(getActiveDb(), async (transaction) => {
      const accountRef = doc(getActiveDb(), 'accounts', userId)
      
      // First, remove default flag from all other profiles
      const userProfiles = await getUserProfiles(userId)
      for (const profile of userProfiles) {
        if (profile.profileId !== profileId && profile.default) {
          const otherProfileRef = doc(getActiveDb(), 'profiles', profile.profileId)
          transaction.update(otherProfileRef, {
            default: false,
            updated_at: serverTimestamp()
          })
        }
      }
      
      // Set the new profile as default
      transaction.update(profileRef, {
        default: true,
        is_default: true, // Ensure both fields are set
        updated_at: serverTimestamp()
      })
      
      // Update the account's default_pid
      transaction.update(accountRef, {
        default_pid: profileId,
        updated_at: serverTimestamp()
      })
    })
    
  } catch (error) {
    console.error('Error setting profile as default:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Failed to set profile as default')
  }
}

/**
 * Clear the default flag from a profile
 * This removes the default property from the profile and clears the account's default_pid if it matches
 */
export const clearProfileAsDefault = async (userId: string, profileId: string): Promise<void> => {
  try {
    // Verify the profile exists and belongs to the user
    const profileRef = doc(getActiveDb(), 'profiles', profileId)
    const profileSnap = await getDoc(profileRef)
    
    if (!profileSnap.exists()) {
      throw new Error('Profile not found')
    }
    
    const rawProfileData = profileSnap.data()
    const profileData = transformKeysToCamel(rawProfileData) as ProfileDocument
    
    if (profileData.ownerUid !== userId) {
      throw new Error('You do not have permission to modify this profile')
    }
    
    // Use a transaction to ensure atomicity
    await runTransaction(getActiveDb(), async (transaction) => {
      const accountRef = doc(getActiveDb(), 'accounts', userId)
      const accountSnap = await transaction.get(accountRef)
      
      // Clear the default flag from this profile
      transaction.update(profileRef, {
        default: false,
        is_default: false, // Ensure both fields are cleared
        updated_at: serverTimestamp()
      })
      
      // If this profile was the account's default_pid, clear it
      if (accountSnap.exists()) {
        const accountData = accountSnap.data()
        const camelAccountData = transformKeysToCamel(accountData) as AccountDocument
        if (camelAccountData.defaultProfileId === profileId) {
          transaction.update(accountRef, {
            default_pid: deleteField(),
            updated_at: serverTimestamp()
          })
        }
      }
    })
    
  } catch (error) {
    console.error('Error clearing profile as default:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Failed to clear profile as default')
  }
}

/**
 * Delete an entire user account and all associated data
 * This function handles the complete deletion of a user account including:
 * - Account document
 * - All user's profiles and their subcollections
 * - All username documents for the user
 * - Profile access entries from other users' accounts
 * - Any other user-specific data
 */
/**
 * Get legacy account information (V2/V3)
 * 
 * @param userId - User ID to check
 * @param isAdmin - If true, uses admin endpoint (for admin users checking other accounts)
 */
export const getLegacyAccountInfo = async (userId: string, isAdmin: boolean = false): Promise<{
  has_v2: boolean
  has_v3: boolean
  v2_user_id?: string
  v3_user_id?: string
  v3_auth_uid?: string
}> => {
  try {
    if (!getActiveAuth().currentUser) {
      throw new Error('You must be logged in')
    }

    const token = await getActiveAuth().currentUser.getIdToken()
    const endpoint = isAdmin 
      ? `${API_BASE_URL}/api/v1/admin/users/${userId}/legacy-info`
      : `${API_BASE_URL}/api/v1/users/${userId}/legacy-info`
    
    const resp = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err?.detail || 'Failed to get legacy account info')
    }

    return await resp.json()
  } catch (error) {
    console.error('Error getting legacy account info:', error)
    // Return safe defaults on error
    return {
      has_v2: false,
      has_v3: false
    }
  }
}

/**
 * Delete an entire user account and all associated data
 * This function handles the complete deletion of a user account including:
 * - Account document
 * - All user's profiles and their subcollections
 * - All username documents for the user
 * - Profile access entries from other users' accounts
 * - Any other user-specific data
 * 
 * @param userId - User ID to delete
 * @param deleteV2 - If true, also delete V2 legacy account (default: false)
 * @param deleteV3 - If true, also delete V3 legacy account (default: false)
 */
export const deleteAccount = async (userId: string, deleteV2: boolean = false, deleteV3: boolean = false): Promise<void> => {
  try {
    // Ensure requester is the same user
    if (!getActiveAuth().currentUser || getActiveAuth().currentUser.uid !== userId) {
      throw new Error('You do not have permission to delete this account')
    }

    const token = await getActiveAuth().currentUser.getIdToken()
    const params = new URLSearchParams()
    if (deleteV2) params.set('delete_v2', 'true')
    if (deleteV3) params.set('delete_v3', 'true')
    const query = params.toString()

    // Schedule deletion (30-day grace period); user is signed out after scheduling.
    const resp = await fetch(
      `${API_BASE_URL}/api/v1/users/account${query ? `?${query}` : ''}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        },
        signal: AbortSignal.timeout(60_000)
      }
    )

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      const detail = err?.detail
      throw new Error(
        typeof detail === 'string' ? detail : 'Failed to delete account'
      )
    }
  } catch (error) {
    console.error('Error deleting account:', error)
    throw error
  }
}

/**
 * Delete all profile access entries for a specific user
 */
const deleteUserProfileAccessEntries = async (userId: string): Promise<void> => {
  const profileAccessRef = collection(getActiveDb(), 'accounts', userId, 'profile_access')
  const profileAccessSnapshot = await getDocs(profileAccessRef)
  
  if (profileAccessSnapshot.empty) return
  
  const batch = writeBatch(getActiveDb())
  
  profileAccessSnapshot.docs.forEach(doc => {
    batch.delete(doc.ref)
  })
  
  await batch.commit()
}

/**
 * Check if an account can be deleted
 * Returns validation info about whether the account can be deleted
 */
export const canDeleteAccount = async (userId: string): Promise<{
  canDelete: boolean
  reason?: string
}> => {
  try {
    if (!getActiveAuth().currentUser) {
      return { canDelete: false, reason: 'User not authenticated' }
    }
    
    // Check if user owns this account
    if (getActiveAuth().currentUser.uid !== userId) {
      return { canDelete: false, reason: 'You do not have permission to delete this account' }
    }
    
    // Get account data
    const accountRef = doc(getActiveDb(), 'accounts', userId)
    const accountSnap = await getDoc(accountRef)
    
    if (!accountSnap.exists()) {
      return { canDelete: false, reason: 'Account not found' }
    }
    
    // For now, all accounts can be deleted (no special restrictions)
    // In the future, we might add restrictions like:
    // - Active subscriptions that need to be cancelled first
    // - Shared profiles with other users
    // - Administrative accounts
    
    return { canDelete: true }
  } catch (error) {
    console.error('Error checking if account can be deleted:', error)
    return { canDelete: false, reason: 'Error checking account deletion permissions' }
  }
}

/**
 * Get account deletion info
 * Provides comprehensive information about what will be deleted
 */
export const getAccountDeletionInfo = async (userId: string): Promise<{
  canDelete: boolean
  reason?: string
  profileCount: number
  warningMessage: string
}> => {
  try {
    if (!getActiveAuth().currentUser) {
      return {
        canDelete: false,
        reason: 'User not authenticated',
        profileCount: 0,
        warningMessage: 'You must be logged in to delete an account'
      }
    }

    // Check deletion eligibility
    const deleteCheck = await canDeleteAccount(userId)
    
    // Get user's profiles count
    const userProfiles = await getUserProfiles(userId)
    
    const warningMessage = `
Deleting your account will permanently remove ALL data including:

PROFILES (${userProfiles.length} profile${userProfiles.length === 1 ? '' : 's'}):
${userProfiles.map(p => `• ${p.name} (@${p.username})`).join('\n')}

ALL DATA WILL BE DELETED:
• Genetic reports and analysis
• Raw genetic data files (DNA uploads)
• Wellness goals and tracking data
• Chat conversation history
• Personal health records
• Medical history and conditions
• Profile information and settings
• Account preferences and notifications
• Subscription and billing data
• Activity logs and usage data

This action CANNOT be undone. All data will be permanently lost.

To proceed, type "DELETE" to confirm.
    `.trim()

    return {
      canDelete: deleteCheck.canDelete,
      reason: deleteCheck.reason,
      profileCount: userProfiles.length,
      warningMessage
    }

  } catch (error) {
    console.error('Error getting account deletion info:', error)
    return {
      canDelete: false,
      reason: 'Error checking account deletion permissions',
      profileCount: 0,
      warningMessage: 'Unable to determine account deletion information at this time'
    }
  }
}

/**
 * Delete profile data from all external databases (GCS, BigQuery, Pinecone)
 * This function calls the backend to clean up data from external services
 */
const deleteProfileExternalData = async (profileId: string): Promise<void> => {
  try {
    
    // Call backend endpoint to delete from all external databases
    const response = await fetch(`${API_BASE_URL}/api/v1/external-data/profiles/delete-external-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        profile_id: profileId
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to delete external data')
    }

    const result = await response.json()
    
  } catch (error) {
    console.error(`Error deleting external data for profile ${profileId}:`, error)
    // Don't throw here - we want to continue with Firestore deletion even if external deletion fails
    // The user should be warned but not blocked from deleting the profile
  }
}

/**
 * Delete account data from all external databases (GCS, BigQuery, Pinecone)
 * This function calls the backend to clean up data from external services
 */
const deleteAccountExternalData = async (userId: string): Promise<void> => {
  try {
    
      // Get user's profile IDs first
      const profileIds: string[] = []
      try {
        const profilesSnapshot = await getDocs(query(
          collection(getActiveDb(), 'profiles'),
        where('owner_uid', '==', userId)
      ))
      
      profilesSnapshot.forEach(doc => {
        profileIds.push(doc.id)
      })
      
    } catch (error) {
      console.error('Error getting profile IDs:', error)
      // Continue with empty profile IDs rather than failing
    }
    
    // Call backend endpoint to delete from all external databases
    const response = await fetch(`${API_BASE_URL}/api/v1/external-data/accounts/delete-external-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        profile_ids: profileIds
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to delete external data')
    }

    const result = await response.json()
    
  } catch (error) {
    console.error(`Error deleting external data for account ${userId}:`, error)
    // Don't throw here - we want to continue with Firestore deletion even if external deletion fails
    // The user should be warned but not blocked from deleting the account
  }
} 
